import { apiFetch } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import { type Asset, type AssetId, AssetSchema } from "@storytell/types";
import { SPREADSHEET_MIME_TYPE_LIST } from "@storytell/types/spreadsheet";
import { createSignal, onCleanup, onMount } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { isServer } from "solid-js/web";
import { LRUCache } from "../../lib/lruCache";

const LRU_CACHE_SIZE = 100;
const RETRY_DELAY_MS = 500;
// Keep ?ids= URLs under ~4 KB so we don't trip proxy/CDN URL length limits.
// AssetId is "asset_" + 20 chars (26 bytes) + "&ids=" delimiter (5 bytes) = ~31 B/id;
// 100 * 31 ~= 3.1 KB, leaving headroom for the base URL.
const ASSET_ID_BATCH_SIZE = 100;
// Upper bound on assets fetched per content-type pull. Two bounds hold it: the
// list endpoint rejects a page above 100 outright, and the pull hydrates the
// store, so a page the size of LRU_CACHE_SIZE would evict everything else
// cached. Deliberately a plain literal — deriving it from LRU_CACHE_SIZE would
// push the request past the endpoint's cap the day that grows. Callers that
// need everything would have to paginate; spreadsheet libraries are in the
// dozens for almost all users.
const CONTENT_TYPE_FETCH_LIMIT = 50;

export type AssetsService = ReturnType<typeof createAssetsService>;

/**
 * Dependencies for the assets service.
 */
interface AssetsServiceDeps {
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  assetsSynced: () => void;
}

interface AssetStore {
  byId: Record<string, Asset>;
}

/** Protobuf `assets.v3.AssetLifecycleState` numeric values -> UI string keys (see asset_pb.ts). */
const PROTO_LIFECYCLE_NUMBER_TO_UI: Record<number, Asset["status"]["state"]> = {
  1: "AWAITING_UPLOAD",
  2: "UPLOADED",
  3: "QUEUED_FOR_PROCESSING",
  4: "PROCESSING",
  5: "READY",
  6: "FAILED",
  7: "REPROCESSING",
  8: "ARCHIVED",
  9: "QUARANTINED",
  10: "DELETE_REQUESTED",
  11: "DELETED",
  12: "TOO_MANY_REQUESTS",
};

const VALID_UI_LIFECYCLE = new Set<string>([
  "AWAITING_UPLOAD",
  "UPLOADED",
  "QUEUED_FOR_PROCESSING",
  "PROCESSING",
  "INDEXING",
  "READY",
  "FAILED",
  "REPROCESSING",
  "ARCHIVED",
  "QUARANTINED",
  "DELETE_REQUESTED",
  "DELETED",
  "TOO_MANY_REQUESTS",
]);

/**
 * Coerces API / protobuf lifecycle values to the uppercase string keys the UI expects.
 * Retained for external consumers (e.g. AssetCard) that may receive state values
 * from non-REST sources (Connect/protobuf numeric enums, prefixed strings).
 */
export function normalizeAssetStatusState(raw: unknown): Asset["status"]["state"] {
  if (typeof raw === "number") {
    return PROTO_LIFECYCLE_NUMBER_TO_UI[raw] ?? "READY";
  }
  if (typeof raw === "string") {
    let s = raw.trim();
    if (!s) return "READY";
    const prefix = "ASSET_LIFECYCLE_STATE_";
    if (s.startsWith(prefix)) {
      s = s.slice(prefix.length);
    }
    s = s.toUpperCase();
    if (s === "UNSPECIFIED") return "READY";
    if (VALID_UI_LIFECYCLE.has(s)) return s as Asset["status"]["state"];
    return "READY";
  }
  return "READY";
}

/**
 * Appends "Z" to ISO-like timestamps that lack a timezone suffix,
 * so ECMAScript Date parsing treats them as UTC rather than local time.
 */
function ensureUtcIsoString(iso: string | undefined): string | undefined {
  if (!iso) return iso;
  const s = iso.trim();
  if (!s) return iso;

  if (/[zZ]$/i.test(s)) return s;
  if (/[+-]\d{2}:\d{2}$/.test(s)) return s;
  if (/[+-]\d{4}$/.test(s)) return s;

  const withT = s.includes("T") ? s : s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, "$1T");

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(withT)) {
    return `${withT}Z`;
  }

  return s;
}

/** Milliseconds since Unix epoch; NaN if unparseable. */
export function parseBackendUtcMs(iso: string | undefined): number {
  if (!iso) return Number.NaN;
  const coerced = ensureUtcIsoString(iso) ?? iso;
  return Date.parse(coerced);
}

function parseAsset(raw: unknown): Asset | null {
  const result = AssetSchema.safeParse(raw);
  if (result.success) return result.data;
  return null;
}

function recordsOf(body: unknown): unknown[] {
  return typeof body === "object" && body !== null && "records" in body && Array.isArray(body.records)
    ? body.records
    : [];
}

function countOf(body: unknown): number {
  return Number(typeof body === "object" && body !== null && "count" in body ? (body.count ?? 0) : 0);
}

function parseAssetList(records: unknown[]): Asset[] {
  const assets: Asset[] = [];
  for (const record of records) {
    const asset = parseAsset(record);
    if (asset) assets.push(asset);
  }
  return assets;
}

export const createAssetsService = (deps: AssetsServiceDeps) => {
  // Reactive store for asset data
  const [store, setStore] = createStore<AssetStore>({ byId: {} });

  // LRU cache for tracking which assets to keep
  const lruCache = new LRUCache<string, true>(LRU_CACHE_SIZE);

  // Track in-flight requests to deduplicate
  const inFlightRequests = new Map<string, Promise<Asset | null>>();

  // Track failed asset IDs
  const failedIds = new Set<string>();

  // Track total count from backend (for showing "Powering this chat: X sources")
  const [totalCount, setTotalCount] = createSignal<number | undefined>(undefined);

  // Track spreadsheet count from backend (for accurate file vs spreadsheet split)
  const [spreadsheetCount, setSpreadsheetCount] = createSignal<number | undefined>(undefined);

  /** Deduplicate concurrent fetchTotalCount() (e.g. many chat message renderers on mount). */
  let fetchTotalCountInFlight: Promise<number> | undefined;
  let fetchSpreadsheetCountInFlight: Promise<number> | undefined;
  let fetchSpreadsheetsInFlight: Promise<Asset[]> | undefined;

  /**
   * Evict the least recently used asset from the store if at capacity.
   */
  const evictIfNeeded = () => {
    if (lruCache.size < LRU_CACHE_SIZE) return;

    // Find the oldest entry (first in the LRU cache) that's in the store
    for (const key of lruCache.keys()) {
      if (store.byId[key]) {
        lruCache.delete(key);
        setStore("byId", key, undefined as unknown as Asset);
        break;
      }
    }
  };

  /**
   * Add an asset to the store and LRU cache.
   */
  const addToStore = (asset: Asset) => {
    evictIfNeeded();
    lruCache.set(asset.id, true);
    failedIds.delete(asset.id);
    setStore("byId", asset.id, asset);
  };

  /**
   * Remove an asset from the store and cache.
   */
  const removeFromStore = (id: string) => {
    lruCache.delete(id);
    failedIds.delete(id);
    setStore(
      produce((s) => {
        delete s.byId[id];
      })
    );
  };

  /**
   * Fetch a single asset from the backend.
   */
  const fetchSingleAsset = async (id: string, retry = true): Promise<Asset | null> => {
    const token = deps.authToken();
    if (!token) return null;

    const projectId = deps.projectId();
    if (!projectId) return null;

    try {
      const response = await apiFetch(`/assets/${id}`, {
        scope: "project-scoped",
        authToken: token,
        projectIdOverride: projectId,
        method: "GET",
      });

      if (!response.ok) {
        if (response.status === 404) {
          failedIds.add(id);
          return null;
        }
        throw new Error(`Failed to fetch asset: ${response.status}`);
      }

      const data: unknown = await response.json();
      return parseAsset(data);
    } catch (error) {
      if (retry) {
        // Single retry after delay
        await new Promise((resolve) => {
          setTimeout(resolve, RETRY_DELAY_MS);
        });
        return fetchSingleAsset(id, false);
      }
      console.error(`Failed to fetch asset ${id}:`, error);
      failedIds.add(id);
      return null;
    }
  };

  /**
   * Chunks the request body to avoid oversized payloads. Throws on transport
   * or non-ok response so callers can distinguish "these IDs don't exist"
   * from "the server is unreachable".
   */
  const fetchBulkAssets = async (ids: string[]): Promise<Asset[]> => {
    const token = deps.authToken();
    if (!token || ids.length === 0) return [];

    const projectId = deps.projectId();
    if (!projectId) return [];

    const results: Asset[] = [];
    for (let i = 0; i < ids.length; i += ASSET_ID_BATCH_SIZE) {
      const chunk = ids.slice(i, i + ASSET_ID_BATCH_SIZE);
      const response = await apiFetch(`/assets/bulk`, {
        scope: "project-scoped",
        authToken: token,
        projectIdOverride: projectId,
        method: "POST",
        body: JSON.stringify({ assetIds: chunk }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.status}`);
      }

      const data: unknown = await response.json();
      const records = recordsOf(data);
      for (const asset of parseAssetList(records)) {
        results.push(asset);
      }
    }
    return results;
  };

  /**
   * Search assets using the backend search endpoint.
   */
  const searchAssets = async (query: string, source?: string): Promise<Asset[]> => {
    const token = deps.authToken();
    if (!token) return [];

    const projectId = deps.projectId();
    if (!projectId) return [];

    try {
      const params = new URLSearchParams();
      if (query) {
        params.set("search", query);
      }
      params.set("limit", "50");
      if (source) params.set("source", source);

      const response = await apiFetch(`/assets?${params.toString()}`, {
        scope: "project-scoped",
        authToken: token,
        projectIdOverride: projectId,
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`Failed to search assets: ${response.status}`);
      }

      const data: unknown = await response.json();
      const records = recordsOf(data);
      const assets = parseAssetList(records);

      for (const asset of assets) {
        addToStore(asset);
      }

      return assets;
    } catch (error) {
      console.error("Failed to search assets:", error);
      return [];
    }
  };

  const listAssets = async (options?: {
    limit?: number;
    afterId?: string;
    beforeId?: string;
    sort?: string;
    source?: string;
  }): Promise<{ assets: Asset[]; hasMore: boolean; lastId?: string }> => {
    const token = deps.authToken();
    if (!token) return { assets: [], hasMore: false };

    const projectId = deps.projectId();
    if (!projectId) return { assets: [], hasMore: false };

    try {
      const params = new URLSearchParams();
      if (options?.limit) params.set("limit", String(options.limit));
      if (options?.afterId) params.set("after_id", options.afterId);
      if (options?.beforeId) params.set("before_id", options.beforeId);
      if (options?.sort) params.set("sort", options.sort);
      if (options?.source) params.set("source", options.source);

      const response = await apiFetch(`/assets?${params.toString()}`, {
        scope: "project-scoped",
        authToken: token,
        projectIdOverride: projectId,
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`Failed to list assets: ${response.status}`);
      }

      const data: unknown = await response.json();
      const records = recordsOf(data);
      const assets = parseAssetList(records);

      for (const asset of assets) {
        addToStore(asset);
      }

      const page = typeof data === "object" && data !== null ? data : {};
      return {
        assets,
        hasMore: "has_more" in page && page.has_more === true,
        lastId: "last_id" in page && typeof page.last_id === "string" ? page.last_id : undefined,
      };
    } catch (error) {
      console.error("Failed to list assets:", error);
      return { assets: [], hasMore: false };
    }
  };

  /**
   * Get an asset by ID. Returns the asset from the store (reactive).
   * Triggers a background fetch if the asset is not in the store.
   */
  const get = (id: string): Asset | undefined => {
    if (!id) return undefined;

    // Touch the LRU cache to mark as recently used
    if (lruCache.has(id)) {
      lruCache.get(id);
    }

    // If already in store, return it
    const existing = store.byId[id];
    if (existing) return existing;

    // If already failed, don't retry
    if (failedIds.has(id)) return undefined;

    // If already fetching, return undefined (will update when done)
    if (inFlightRequests.has(id)) return undefined;

    // Start background fetch (only on client)
    if (!isServer) {
      const fetchPromise = fetchSingleAsset(id).then((asset) => {
        inFlightRequests.delete(id);
        if (asset) {
          addToStore(asset);
        }
        return asset;
      });
      inFlightRequests.set(id, fetchPromise);
    }

    return undefined;
  };

  /**
   * Get multiple assets by IDs. Returns what's in the store immediately,
   * triggers background fetch for missing assets.
   */
  const getMany = (ids: string[]): (Asset | undefined)[] => {
    const results: (Asset | undefined)[] = [];
    const missing: string[] = [];

    for (const id of ids) {
      const existing = store.byId[id];
      if (existing) {
        // Touch LRU
        lruCache.get(id);
        results.push(existing);
      } else if (!failedIds.has(id) && !inFlightRequests.has(id)) {
        missing.push(id);
        results.push(undefined);
      } else {
        results.push(undefined);
      }
    }

    // Batch fetch missing assets
    if (missing.length > 0 && !isServer) {
      const fetchPromise = fetchBulkAssets(missing)
        .then((assets) => {
          const fetchedIds = new Set(assets.map((a) => a.id));
          for (const id of missing) {
            inFlightRequests.delete(id);
            if (!fetchedIds.has(id as AssetId)) {
              failedIds.add(id);
            }
          }
          for (const asset of assets) {
            addToStore(asset);
          }
          return assets;
        })
        .catch((error) => {
          console.error("Failed to fetch bulk assets:", error);
          for (const id of missing) {
            inFlightRequests.delete(id);
            failedIds.add(id);
          }
          return [] as Asset[];
        });

      // Mark all as in-flight
      for (const id of missing) {
        inFlightRequests.set(
          id,
          fetchPromise.then((assets) => assets.find((a) => a.id === id) ?? null)
        );
      }
    }

    return results;
  };

  /**
   * Check if an asset fetch failed.
   */
  const hasFailed = (id: string): boolean => {
    return failedIds.has(id);
  };

  /**
   * Bulk-fetch asset IDs and return the set that still exists on the backend.
   * Returns null if the network request itself fails (so callers can distinguish
   * "these specific IDs don't exist" from "we couldn't reach the server").
   * Hydrates the store with returned assets; marks missing IDs as failed.
   *
   * Used to reconcile persisted chat scopes that reference assets which have
   * since been deleted or replaced (e.g. a re-uploaded file gets a new ID).
   */
  const validateAssetIds = async (ids: string[]): Promise<Set<string> | null> => {
    if (ids.length === 0) return new Set();
    if (!deps.authToken()) return null;

    try {
      const assets = await fetchBulkAssets(ids);

      for (const asset of assets) {
        addToStore(asset);
      }

      const validIds = new Set<string>(assets.map((a) => a.id));
      for (const id of ids) {
        if (!validIds.has(id)) {
          failedIds.add(id);
        }
      }
      return validIds;
    } catch (error) {
      console.error("validateAssetIds failed:", error);
      return null;
    }
  };

  /**
   * Force a refetch of an asset.
   */
  const invalidate = (id: string): void => {
    failedIds.delete(id);
    inFlightRequests.delete(id);

    if (!isServer) {
      const fetchPromise = fetchSingleAsset(id).then((asset) => {
        inFlightRequests.delete(id);
        if (asset) {
          addToStore(asset);
        }
        return asset;
      });
      inFlightRequests.set(id, fetchPromise);
    }
  };

  /**
   * Set an asset directly in the store (for optimistic updates).
   */
  const set = (_id: string, asset: Asset): void => {
    addToStore(asset);
  };

  /**
   * Remove an asset from the store.
   */
  const remove = (id: string): void => {
    removeFromStore(id);
  };

  /**
   * Clear all assets from the store.
   */
  const clear = (): void => {
    lruCache.clear();
    failedIds.clear();
    inFlightRequests.clear();
    fetchTotalCountInFlight = undefined;
    fetchSpreadsheetCountInFlight = undefined;
    fetchSpreadsheetsInFlight = undefined;
    setTotalCount(undefined);
    setSpreadsheetCount(undefined);
    setStore({ byId: {} });
  };

  const fetchTotalCount = async (): Promise<number> => {
    const token = deps.authToken();
    if (!token) {
      return 0;
    }

    const projectId = deps.projectId();
    if (!projectId) return 0;

    if (fetchTotalCountInFlight !== undefined) {
      return fetchTotalCountInFlight;
    }

    fetchTotalCountInFlight = (async () => {
      try {
        const response = await apiFetch(`/assets/count`, {
          scope: "project-scoped",
          authToken: token,
          projectIdOverride: projectId,
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch asset count: ${response.status}`);
        }

        const data: unknown = await response.json();
        const count = countOf(data);
        setTotalCount(count);
        return count;
      } catch {
        return 0;
      } finally {
        fetchTotalCountInFlight = undefined;
      }
    })();

    return fetchTotalCountInFlight;
  };

  /**
   * Fetch the count of assets matching specific content types from the backend.
   * Useful for getting e.g. spreadsheet count without loading all assets.
   */
  const fetchCountByContentTypes = async (contentTypes: string[]): Promise<number> => {
    const token = deps.authToken();
    if (!token || contentTypes.length === 0) return 0;

    const projectId = deps.projectId();
    if (!projectId) return 0;

    try {
      const params = new URLSearchParams();
      for (const ct of contentTypes) {
        params.append("content_types", ct);
      }

      const response = await apiFetch(`/assets/count?${params.toString()}`, {
        scope: "project-scoped",
        authToken: token,
        projectIdOverride: projectId,
        method: "GET",
      });

      if (!response.ok) return 0;

      const data: unknown = await response.json();
      return countOf(data);
    } catch {
      return 0;
    }
  };

  // Spreadsheet MIME type list imported from @storytell/types (single source of truth).

  /**
   * Fetch the spreadsheet count from the backend (deduplicated + cached).
   * Uses content_types filter to get an accurate count without loading all assets.
   */
  const fetchSpreadsheetCount = async (): Promise<number> => {
    if (fetchSpreadsheetCountInFlight !== undefined) return fetchSpreadsheetCountInFlight;

    fetchSpreadsheetCountInFlight = fetchCountByContentTypes(SPREADSHEET_MIME_TYPE_LIST as string[]).then((count) => {
      setSpreadsheetCount(count);
      fetchSpreadsheetCountInFlight = undefined;
      return count;
    });

    return fetchSpreadsheetCountInFlight;
  };

  /**
   * Fetch assets matching the given content types and hydrate them into the
   * store. Used to pre-load content-type-filtered sets (e.g. all spreadsheets
   * in a large, paginated project) so filtered views don't show "no results"
   * just because pagination hasn't reached them yet.
   */
  const fetchAssetsByContentTypes = async (
    contentTypes: string[],
    limit: number = CONTENT_TYPE_FETCH_LIMIT
  ): Promise<Asset[]> => {
    const token = deps.authToken();
    if (!token || contentTypes.length === 0) return [];

    const projectId = deps.projectId();
    if (!projectId) return [];

    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      for (const ct of contentTypes) {
        params.append("content_types", ct);
      }
      const response = await apiFetch(`/assets?${params.toString()}`, {
        scope: "project-scoped",
        authToken: token,
        projectIdOverride: projectId,
        method: "GET",
      });
      if (!response.ok) {
        console.warn(`fetchAssetsByContentTypes: ${response.status} for content types`, contentTypes);
        return [];
      }
      const data: unknown = await response.json();
      const records = recordsOf(data);
      const assets = parseAssetList(records);
      for (const asset of assets) {
        addToStore(asset);
      }
      return assets;
    } catch (error) {
      console.error("fetchAssetsByContentTypes failed:", error);
      return [];
    }
  };

  /**
   * Fetch the project's spreadsheets (up to {@link CONTENT_TYPE_FETCH_LIMIT})
   * and hydrate them into the store. Keeps the picker's Spreadsheets tab
   * populated in large projects where pagination wouldn't reach spreadsheet
   * assets. Deduplicated across concurrent callers.
   */
  const fetchSpreadsheets = async (): Promise<Asset[]> => {
    if (fetchSpreadsheetsInFlight !== undefined) return fetchSpreadsheetsInFlight;
    fetchSpreadsheetsInFlight = fetchAssetsByContentTypes(SPREADSHEET_MIME_TYPE_LIST as string[]).finally(() => {
      fetchSpreadsheetsInFlight = undefined;
    });
    return fetchSpreadsheetsInFlight;
  };

  /**
   * Get the spreadsheet count (reactive signal).
   * Returns undefined if not yet fetched. Use fetchSpreadsheetCount() to load it.
   */
  const getSpreadsheetCount = (): number | undefined => {
    return spreadsheetCount();
  };

  /**
   * Get the total count of assets (reactive signal).
   * Returns undefined if not yet fetched. Use fetchTotalCount() to load it.
   */
  const getTotalCount = (): number | undefined => {
    return totalCount();
  };

  /**
   * Get all assets currently in the store.
   * Note: This only returns cached assets, not all assets in the system.
   */
  const all = (): Asset[] => {
    return Object.values(store.byId);
  };

  // --- Legacy compatibility methods ---

  /**
   * Search assets locally in the store (for backward compatibility).
   * Prefer using the async search() method which hits the backend.
   */
  const searchLocal = (query: string): Asset[] => {
    if (!query) return all();
    const lowerQuery = query.toLowerCase().replace(/\s+/g, "");
    return all().filter((asset) => {
      const searchText = `${asset.filename}${asset.name}${asset.description ?? ""}`.toLowerCase().replace(/\s+/g, "");
      return searchText.includes(lowerQuery);
    });
  };

  /**
   * Search IDs locally in the store (for backward compatibility).
   */
  const searchIds = (query: string): string[] => {
    return searchLocal(query).map((a) => a.id);
  };

  // Event handling for surgical updates
  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      // Clear cache on project/user switch
      clear();
    });

    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      // Clear failed IDs so they can be retried
      failedIds.clear();
    });

    const unsubscribeServerEvent = subscribeToServerEvent("cp.assets", (serverEvent) => {
      const assetId = serverEvent.resource.id;

      if (serverEvent.op === "op.delete") {
        // Remove from store
        removeFromStore(assetId);
      } else if (serverEvent.op === "op.create" || serverEvent.op === "op.update") {
        // Fetch the updated asset
        invalidate(assetId);
      }
    });

    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeConnectivityRestored();
      unsubscribeServerEvent();
    });
  });

  // Notify that assets are "synced" (for backward compatibility)
  // In the new lazy-loading model, we're always ready
  if (!isServer) {
    deps.assetsSynced();
  }

  return {
    // New API
    get,
    getMany,
    search: searchAssets,
    listAssets,
    hasFailed,
    validateAssetIds,
    set,
    remove,
    getTotalCount,
    fetchTotalCount,
    getSpreadsheetCount,
    fetchSpreadsheetCount,
    fetchSpreadsheets,

    // Legacy compatibility
    all,
    searchIds,

    // Legacy: refetch (no-op in new architecture)
    refetch: () => {
      // no-op
    },
  };
};
